import { Tooltip } from '@mui/material';
import { ChevronDown, FileText } from 'lucide-react';
import { useState } from 'react';

import { generateDocumentRecordUrl } from '@/services/api/document-records/api';

// Matches the neutral document chips in PropertiesTab's Documents section,
// but remains clickable — opens the
// source document in a new tab the same way RecordsCard's download action
// does (presigned URL via generateDocumentRecordUrl, no dedicated preview
// modal exists in the app today).
export interface DocumentCitation {
  documentRecordId: string;
  label: string;
  primaryPage?: number;
  /** Stable identity for one logical document inside a compiled PDF. */
  manifestItemId?: string;
}

/** Shared open-in-new-tab behavior for anything that links back to a source
 *  document — the citation chip below, and any other clickable
 *  document-record UI (e.g. a checklist row) that wants the same presigned-
 *  URL flow without its own copy of the fetch/loading logic. Tracks which
 *  source key is currently opening (not just a boolean) so logical documents
 *  sharing one compiled PDF retain independent loading states. */
export function useOpenDocumentRecord() {
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  const open = async (documentRecordId: string, primaryPage?: number, openingKey?: string) => {
    if (openingId) return;
    setOpenError(null);
    setOpeningId(openingKey ?? documentRecordId);
    try {
      const url = await generateDocumentRecordUrl({ id: documentRecordId });
      const pageAnchor = primaryPage && primaryPage > 1 ? `#page=${primaryPage}` : '';
      window.open(`${url}${pageAnchor}`, '_blank', 'noopener,noreferrer');
    } catch {
      setOpenError('Unable to open this document. Please try again.');
    } finally {
      setOpeningId(null);
    }
  };

  return { open, openingId, openError };
}

const DOCUMENT_ACRONYMS: Record<string, string> = {
  hoa: 'HOA',
  'cc&rs': 'CC&Rs',
  'cc&r': 'CC&R',
  ccrs: 'CCRs',
  ccr: 'CCR',
  pdf: 'PDF',
  llc: 'LLC',
  llp: 'LLP',
  fha: 'FHA',
  va: 'VA',
  hud: 'HUD',
  fema: 'FEMA',
  'd&o': 'D&O',
};

export function formatDocumentTitle(label: string): string {
  const words = [...label.matchAll(/[\p{L}\p{N}]+(?:[&’'][\p{L}\p{N}]+)*/gu)];
  let index = 0;
  return label.replace(/[\p{L}\p{N}]+(?:[&’'][\p{L}\p{N}]+)*/gu, (word) => {
    const lower = word.toLowerCase();
    const isEdge = index === 0 || index === words.length - 1;
    index += 1;
    return (
      DOCUMENT_ACRONYMS[lower] ??
      (!isEdge && /^(a|an|and|as|at|but|by|for|in|nor|of|on|or|per|the|to|vs|via)$/.test(lower)
        ? lower
        : lower.charAt(0).toUpperCase() + lower.slice(1))
    );
  });
}

function DocumentCitationChip({
  documentRecordId,
  label,
  primaryPage,
  manifestItemId,
}: DocumentCitation) {
  const { open, openingId, openError } = useOpenDocumentRecord();
  const openingKey = manifestItemId ?? documentRecordId;
  const loading = openingId === openingKey;
  const title = formatDocumentTitle(label);

  // a source is a quiet link row: hairline document glyph, the full title (two lines at most), its page in faint
  return (
    <Tooltip title={openError ?? title} describeChild enterDelay={400} placement="top" arrow>
      <button
        type="button"
        className="hx-source-row"
        onClick={() => open(documentRecordId, primaryPage, openingKey)}
        disabled={loading}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          padding: '5px 0',
          border: 0,
          background: 'transparent',
          color: 'var(--tx-muted)',
          fontSize: 12,
          lineHeight: 1.35,
          fontWeight: 500,
          fontFamily: "'Manrope', sans-serif",
          textAlign: 'left',
          minWidth: 0,
          cursor: loading ? 'default' : 'pointer',
          opacity: loading ? 0.6 : 1,
        }}>
        <FileText size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: 1, color: 'var(--tx-faint)' }} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}>
          {loading ? 'Opening…' : title}
        </span>
        {primaryPage != null && primaryPage > 1 && (
          <span aria-hidden="true" style={{ flexShrink: 0, color: 'var(--tx-faint)', fontSize: 11 }}>p. {primaryPage}</span>
        )}
      </button>
    </Tooltip>
  );
}

/** Bottom-left source citation block for a ModuleCard. Renders nothing when
 *  there's no linked document — a card with no citations shouldn't reserve
 *  empty footer space. Sits at the card's bottom via the parent's own
 *  height:100% flex column (see ModuleCard) — this component just needs
 *  `marginTop: auto` to get pushed there without extra layout plumbing.
 *  Pass `pushToBottom={false}` when embedding this inside a footer row you're
 *  already positioning yourself (e.g. alongside a disclaimer on the same line).
 *  A short set lists its documents outright; a long one folds behind a
 *  "Sources" toggle so a card ends on one quiet line instead of a chip wall. */
export function DocumentCitationRow({
  citations,
  pushToBottom = true,
}: {
  citations: DocumentCitation[];
  pushToBottom?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (citations.length === 0) return null;

  const canExpand = citations.length > 3;
  const showList = !canExpand || expanded;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 4,
        width: '100%',
        marginTop: pushToBottom ? 'auto' : undefined,
        paddingTop: 10,
      }}>
      {canExpand && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Show fewer documents' : `Show all ${citations.length} documents`}
          onClick={() => setExpanded((current) => !current)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 8,
            padding: '4px 0',
            border: 0,
            background: 'transparent',
            color: 'var(--tx-muted)',
            fontSize: 12,
            fontWeight: 500,
            fontFamily: "'Manrope', sans-serif",
            cursor: 'pointer',
          }}>
          <FileText size={14} aria-hidden="true" style={{ color: 'var(--tx-faint)' }} />
          Sources
          <span style={{ color: 'var(--tx-faint)', fontSize: 11 }}>{citations.length} documents</span>
          <ChevronDown
            size={14}
            aria-hidden="true"
            style={{
              color: 'var(--tx-faint)',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 160ms ease',
            }}
          />
        </button>
      )}
      {showList && (
        <div
          data-testid="document-citation-list"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            columnGap: 20,
            width: '100%',
            paddingLeft: canExpand ? 22 : 0,
          }}>
          {citations.map((c) => (
            <DocumentCitationChip
              key={c.manifestItemId ?? `${c.documentRecordId}:${c.primaryPage ?? 1}:${c.label}`}
              documentRecordId={c.documentRecordId}
              label={c.label}
              primaryPage={c.primaryPage}
              manifestItemId={c.manifestItemId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
